import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { Trophy, Loader2, Crown, Medal, ArrowRight, MapPin } from 'lucide-react';

function rankStyles(rank) {
  if (rank === 1) return { bg: 'bg-[#C8922F]', tint: 'text-white', icon: Crown };
  if (rank === 2) return { bg: 'bg-slate-300', tint: 'text-[#17352D]', icon: Medal };
  if (rank === 3) return { bg: 'bg-[#B08D57]', tint: 'text-white', icon: Medal };
  return { bg: 'bg-slate-100', tint: 'text-slate-600', icon: Trophy };
}

export default function AffiliateLeaderboardPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get('/affiliate/leaderboard?limit=50').then(r => setRows(r.data.rows || [])).catch(() => setRows([]));
  }, []);

  return (
    <Layout>
      <PageHeader
        eyebrow="Affiliate Program"
        title="Top Affiliates — All Time"
        subtitle="Live leaderboard of the reps closing the most business for Royal Palm Services."
      />
      <section className="max-w-5xl mx-auto px-4 py-10" data-testid="affiliate-leaderboard-page">
        {rows === null ? (
          <div className="py-16 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-slate-300" /></div>
        ) : rows.length === 0 ? (
          <Card className="rounded-3xl border-[#E6E9EE]">
            <CardContent className="p-8 text-center">
              <Trophy className="h-12 w-12 mx-auto text-slate-300" />
              <h2 className="font-headline text-2xl text-[#17352D] mt-3">The leaderboard opens soon</h2>
              <p className="text-slate-600 mt-2">Be the first affiliate on the board — apply now and start closing.</p>
              <Button asChild className="mt-5 rounded-xl bg-[#17352D] text-white"><Link to="/affiliate/apply">Apply Now <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Podium — top 3 */}
            {rows.slice(0, 3).length > 0 && (
              <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8">
                {[1, 0, 2].map(i => {
                  const r = rows[i];
                  if (!r) return <div key={i} />;
                  const st = rankStyles(r.rank);
                  const h = r.rank === 1 ? 'md:h-56' : r.rank === 2 ? 'md:h-48' : 'md:h-44';
                  return (
                    <Card key={r.code} className={`rounded-3xl border-[#E6E9EE] ${h}`}>
                      <CardContent className="p-4 md:p-5 h-full flex flex-col items-center justify-center text-center">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${st.bg} ${st.tint} mb-2`}>
                          <st.icon className="h-5 w-5" />
                        </div>
                        <div className="font-headline text-lg text-[#17352D] leading-tight">{r.name}</div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{r.city || '—'}</div>
                        <div className="mt-2 font-headline text-2xl text-[#C8922F]">${r.lifetime_earned.toFixed(0)}</div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-500">{r.lifetime_referrals} referrals</div>
                        <Badge className="mt-2 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-100">#{r.rank}</Badge>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Full table */}
            <Card className="rounded-3xl border-[#E6E9EE]">
              <CardContent className="p-0">
                <div className="px-4 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold bg-[#F7F8FA] flex items-center gap-4">
                  <div className="w-10">Rank</div>
                  <div className="flex-1">Rep</div>
                  <div className="w-24 text-right hidden md:block">Referrals</div>
                  <div className="w-28 text-right">Earned</div>
                </div>
                <div className="divide-y divide-[#E6E9EE]">
                  {rows.map(r => {
                    const st = rankStyles(r.rank);
                    return (
                      <div key={r.code} className="px-4 py-3 flex items-center gap-4 hover:bg-[#F7F8FA]" data-testid={`leaderboard-row-${r.rank}`}>
                        <div className={`w-10 h-8 flex items-center justify-center rounded-lg text-xs font-bold ${st.bg} ${st.tint}`}>#{r.rank}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#17352D] truncate">{r.name}</div>
                          <div className="text-xs text-slate-500 truncate">{r.city || '—'}</div>
                        </div>
                        <div className="w-24 text-right text-sm text-slate-700 hidden md:block">{r.lifetime_referrals}</div>
                        <div className="w-28 text-right font-headline text-lg text-[#C8922F]">${r.lifetime_earned.toFixed(0)}</div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="text-center mt-8">
              <Button asChild className="rounded-xl bg-[#17352D] text-white"><Link to="/affiliate/apply">Get On The Board <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </div>
          </>
        )}
      </section>
    </Layout>
  );
}
